import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  ArrowLeft,
  Calculator,
  DollarSign,
  Percent,
  Plane,
  Wallet,
  Info,
  Copy,
  FileText,
  Search,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DepositCalculator = () => {
  const [data, setData] = useState({
    grossPrice: '',
    flightCost: '',
    depositPercentage: '25',
  });

  const [results, setResults] = useState(null);

  // "Use on Practice" feature
  const [showPracticeDialog, setShowPracticeDialog] = useState(false);
  const [practices, setPractices] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [practiceSearch, setPracticeSearch] = useState('');
  const [applyMode, setApplyMode] = useState('total'); // 'total' = totaleBonifico, 'acconto' = solo acconto
  const [applyingId, setApplyingId] = useState(null);

  useEffect(() => {
    if (showPracticeDialog && practices.length === 0) {
      fetchPractices();
    }
  }, [showPracticeDialog]);

  const fetchPractices = async () => {
    setLoadingPractices(true);
    try {
      const res = await axios.get(`${API}/trip-admin/list`);
      setPractices(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento delle pratiche');
    } finally {
      setLoadingPractices(false);
    }
  };

  const applyToPractice = async (practice) => {
    if (!results) return;
    const value = applyMode === 'total' ? results.totaleBonifico : results.acconto;
    setApplyingId(practice.admin_id);
    try {
      await axios.put(`${API}/trip-admin/${practice.admin_id}`, {
        confirmation_deposit: parseFloat(value.toFixed(2)),
      });
      toast.success(
        `Acconto conferma aggiornato sulla pratica ${practice.practice_number || practice.trip_title}`
      );
      setShowPracticeDialog(false);
      // Refresh list so the visible deposit value updates next time
      fetchPractices();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Errore durante l\'aggiornamento della pratica');
    } finally {
      setApplyingId(null);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const formatPercentage = (n) => `${(n || 0).toFixed(2)}%`;

  const handleCalculate = () => {
    const gross = parseFloat(data.grossPrice) || 0;
    const flight = parseFloat(data.flightCost) || 0;
    const pct = parseFloat(data.depositPercentage) || 0;

    if (gross <= 0) {
      toast.error('Inserisci un Prezzo Lordo maggiore di zero');
      return;
    }
    if (flight < 0 || flight > gross) {
      toast.error('Il Costo Volo deve essere positivo e minore o uguale al Prezzo Lordo');
      return;
    }
    if (pct < 0 || pct > 100) {
      toast.error('La Percentuale di Acconto deve essere tra 0 e 100');
      return;
    }

    const baseImponibile = gross - flight;
    const acconto = baseImponibile * (pct / 100);
    const totaleBonifico = acconto + flight;
    const saldoResiduo = gross - totaleBonifico;

    setResults({
      gross,
      flight,
      pct,
      baseImponibile,
      acconto,
      totaleBonifico,
      saldoResiduo,
    });
  };

  const handleReset = () => {
    setData({ grossPrice: '', flightCost: '', depositPercentage: '25' });
    setResults(null);
  };

  const handleCopy = (label, value) => {
    navigator.clipboard.writeText(value.toString().replace('.', ','));
    toast.success(`${label} copiato negli appunti`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="deposit-calculator-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="mr-4" data-testid="back-to-dashboard">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <Wallet className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-xl font-bold text-slate-800">Calcolatore Acconto Cliente</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Quanto deve bonificare il cliente?</h2>
          <p className="text-slate-600">
            Calcola la percentuale di acconto sulla quota lorda escluso il volo e somma il costo del volo per
            ottenere l'importo totale da bonificare alla conferma della pratica.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Dati Pratica
              </CardTitle>
              <CardDescription>Inserisci gli importi della pratica e la percentuale di acconto</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="grossPrice">Prezzo Lordo (€) *</Label>
                <Input
                  id="grossPrice"
                  data-testid="input-gross-price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="2500.00"
                  value={data.grossPrice}
                  onChange={(e) => setData({ ...data, grossPrice: e.target.value })}
                />
                <p className="text-xs text-slate-500">Prezzo totale del viaggio venduto al cliente</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="flightCost" className="flex items-center gap-1">
                  <Plane className="w-3 h-3" /> Costo Volo (€)
                </Label>
                <Input
                  id="flightCost"
                  data-testid="input-flight-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="450.00"
                  value={data.flightCost}
                  onChange={(e) => setData({ ...data, flightCost: e.target.value })}
                />
                <p className="text-xs text-slate-500">
                  Il volo viene escluso dal calcolo dell'acconto (di solito va pagato per intero subito)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="depositPercentage" className="flex items-center gap-1">
                  <Percent className="w-3 h-3" /> Percentuale di Acconto (%)
                </Label>
                <Input
                  id="depositPercentage"
                  data-testid="input-deposit-percentage"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="25"
                  value={data.depositPercentage}
                  onChange={(e) => setData({ ...data, depositPercentage: e.target.value })}
                />
                <p className="text-xs text-slate-500">
                  Percentuale richiesta dal Tour Operator per la conferma pratica (tipicamente 25% o 30%)
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCalculate}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="calculate-deposit-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calcola Acconto
                </Button>
                <Button variant="outline" onClick={handleReset} data-testid="reset-deposit-btn">
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Risultato
              </CardTitle>
              <CardDescription>Importo che il cliente deve bonificare</CardDescription>
            </CardHeader>
            <CardContent>
              {!results ? (
                <div className="text-center py-12 text-slate-400" data-testid="results-empty">
                  <Calculator className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Inserisci i dati e clicca su "Calcola Acconto"</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="results-area">
                  {/* Highlight: Totale da bonificare */}
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-5 shadow-md">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-orange-100 font-semibold">
                          Totale da Bonificare al Cliente
                        </p>
                        <p className="text-4xl font-bold mt-2" data-testid="result-total-transfer">
                          {formatCurrency(results.totaleBonifico)}
                        </p>
                        <p className="text-xs text-orange-100 mt-2">
                          = {formatCurrency(results.acconto)} acconto + {formatCurrency(results.flight)} volo
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-orange-700/30"
                        onClick={() => handleCopy('Totale bonifico', results.totaleBonifico.toFixed(2))}
                        data-testid="copy-total-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Use in Practice */}
                  <Button
                    onClick={() => setShowPracticeDialog(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="use-in-practice-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Usa nella Pratica
                  </Button>

                  {/* Breakdown grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                      <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold">Prezzo Lordo</p>
                      <p className="text-xl font-bold text-blue-900 mt-1">{formatCurrency(results.gross)}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <p className="text-xs text-slate-600 uppercase tracking-wide font-semibold flex items-center gap-1">
                        <Plane className="w-3 h-3" /> Costo Volo
                      </p>
                      <p className="text-xl font-bold text-slate-800 mt-1">{formatCurrency(results.flight)}</p>
                    </div>
                    <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                      <p className="text-xs text-indigo-600 uppercase tracking-wide font-semibold">
                        Base Imponibile Acconto
                      </p>
                      <p className="text-xl font-bold text-indigo-900 mt-1">
                        {formatCurrency(results.baseImponibile)}
                      </p>
                      <p className="text-xs text-indigo-600 mt-1">Lordo − Volo</p>
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                      <p className="text-xs text-green-600 uppercase tracking-wide font-semibold">
                        Acconto ({formatPercentage(results.pct)})
                      </p>
                      <p className="text-xl font-bold text-green-900 mt-1" data-testid="result-deposit">
                        {formatCurrency(results.acconto)}
                      </p>
                    </div>
                  </div>

                  {/* Saldo residuo */}
                  <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">
                        Saldo residuo a carico cliente
                      </p>
                      <p className="text-lg font-bold text-slate-800 mt-1" data-testid="result-balance">
                        {formatCurrency(results.saldoResiduo)}
                      </p>
                    </div>
                    <Info className="w-5 h-5 text-slate-400" />
                  </div>

                  <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>
                      <strong>Formula:</strong> ((Prezzo Lordo − Costo Volo) × % Acconto) + Costo Volo = Totale da
                      bonificare.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Apply to Practice Dialog */}
      <Dialog open={showPracticeDialog} onOpenChange={setShowPracticeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="practice-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Applica acconto a una Pratica
            </DialogTitle>
            <DialogDescription>
              Seleziona una pratica e il valore verrà scritto nel campo "Acconto Conferma" della Scheda Finanziaria.
              Verrà anche aggiornata la registrazione del pagamento acconto.
            </DialogDescription>
          </DialogHeader>

          {/* Apply mode toggle */}
          {results && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200">
              <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">
                Quale importo vuoi applicare?
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setApplyMode('total')}
                  data-testid="apply-mode-total"
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    applyMode === 'total'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <div className="text-xs text-slate-500">Totale da bonificare</div>
                  <div className="text-lg font-bold text-orange-700">
                    {formatCurrency(results.totaleBonifico)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Acconto + Costo Volo</div>
                </button>
                <button
                  type="button"
                  onClick={() => setApplyMode('acconto')}
                  data-testid="apply-mode-acconto"
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    applyMode === 'acconto'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <div className="text-xs text-slate-500">Solo acconto (% sul lordo − volo)</div>
                  <div className="text-lg font-bold text-green-700">
                    {formatCurrency(results.acconto)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Volo escluso</div>
                </button>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="practice-search-input"
              placeholder="Cerca per pratica, booking, cliente o destinazione..."
              className="pl-9"
              value={practiceSearch}
              onChange={(e) => setPracticeSearch(e.target.value)}
            />
          </div>

          {/* Practice list */}
          <div className="max-h-[400px] overflow-y-auto -mx-2 px-2">
            {loadingPractices ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (() => {
              const q = practiceSearch.trim().toLowerCase();
              const filtered = q
                ? practices.filter((p) =>
                    [p.practice_number, p.booking_number, p.client_name, p.trip_title, p.destination]
                      .filter(Boolean)
                      .some((f) => String(f).toLowerCase().includes(q))
                  )
                : practices;
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-10 text-slate-500" data-testid="practice-empty">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">
                      {practices.length === 0
                        ? 'Nessuna pratica disponibile. Creane una dalla sezione Viaggi.'
                        : 'Nessuna pratica corrisponde alla ricerca'}
                    </p>
                  </div>
                );
              }
              return (
                <div className="space-y-2">
                  {filtered.map((p) => (
                    <div
                      key={p.admin_id}
                      data-testid={`practice-row-${p.admin_id}`}
                      className="flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800 truncate">
                            {p.trip_title || p.practice_number || 'Pratica'}
                          </span>
                          {p.practice_number && (
                            <Badge variant="outline" className="text-xs">
                              {p.practice_number}
                            </Badge>
                          )}
                          {p.status === 'confirmed' && (
                            <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100">
                              Confermata
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate mt-0.5">
                          {p.client_name} · {p.destination || '—'} · Lordo {formatCurrency(p.gross_amount)}
                          {p.confirmation_deposit > 0 && (
                            <> · Acconto attuale: <strong>{formatCurrency(p.confirmation_deposit)}</strong></>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => applyToPractice(p)}
                        disabled={applyingId === p.admin_id}
                        data-testid={`apply-to-practice-${p.admin_id}`}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {applyingId === p.admin_id ? 'Applico...' : 'Applica'}
                      </Button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPracticeDialog(false)}
              data-testid="close-practice-picker"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepositCalculator;
